import React, { useEffect, useState } from "react";
import { audio } from "services/AudioManager";
import { audioDiag } from "services/audioDiagnostics";
// Same parser as the perf panel, so the two never disagree about the device.
import { deviceLabel } from "components/Shared/PerfOverlay/PerfOverlay";
import styles from "./AudioDebugOverlay.module.scss";

/**
 * AudioDebugOverlay — what the audio system is doing, on the TV itself.
 *
 * There are no devtools on a Tizen set, and the failure that matters most is
 * one that produces no visible symptom beyond silence: a clip too big to
 * decode, which Howler reports as `loaderror` and which otherwise vanishes.
 * This renders that evidence on the screen you're already looking at.
 *
 * ON by default. Flip AUDIO_DEBUG below to false to hide it.
 *
 * WHAT TO READ. The budget line is the diagnosis: `decoded` is the estimated
 * PCM the page is holding. If that number climbs and `load-failed` lines start
 * appearing, the device is out of room and the fix is a smaller resident set
 * (a failed decode switches long clips to streaming). `empty` counts clips that
 * were created but hold nothing — those are the ones that will be silent when the game asks for them.
 */

/** Show the overlay. Set to false to hide it. */
export const AUDIO_DEBUG = false;

const mb = (bytes) => `${(bytes / 1048576).toFixed(1)}MB`;

// HTMLMediaElement.readyState / networkState, named. A stream stuck below
// HAVE_FUTURE_DATA is one the device won't buffer; one sitting AT it with no
// canplaythrough is one Howler is waiting on unnecessarily. Different faults,
// same silence, so the overlay has to tell them apart.
const READY_STATE = ["NOTHING", "METADATA", "CURRENT", "FUTURE", "ENOUGH"];
const NETWORK_STATE = ["EMPTY", "IDLE", "LOADING", "NO_SOURCE"];
const named = (names, n) => (names[n] ? `${n}:${names[n]}` : String(n));

export default function AudioDebugOverlay() {
    // A counter, not the data: the diagnostics object is a mutable singleton,
    // so a new identity would never arrive. Bumping this is what re-renders.
    const [, setTick] = useState(0);
    const [stats, setStats] = useState(() => audio.cacheStats());

    useEffect(() => {
        const refresh = () => {
            setStats(audio.cacheStats());
            setTick(t => t + 1);
        };
        const unsubscribe = audioDiag.subscribe(refresh);
        // The cache also changes without an event (a clip finishing its load
        // through a path that only bumps a counter), so poll it slowly too.
        const poll = setInterval(refresh, 2000);
        refresh();
        return () => { unsubscribe(); clearInterval(poll); };
    }, []);

    const { counts } = audioDiag;
    // Newest first — on a TV you read the top of the list, not the bottom.
    const events = [...audioDiag.events].reverse();
    // Howler suspends the context after ~30s with nothing playing and resumes
    // it on the next play, so "suspended" while idle is normal and not a fault.
    // Saying so stops it reading as the cause when audio IS broken.
    const nothingPlaying = audio.activeMusic.length + audio.activeSfx.length + audio.activeVo.length === 0;
    const rawCtx = audio.contextState();
    const ctxState = rawCtx === "suspended" && nothingPlaying ? "suspended (idle, normal)" : rawCtx;
    const streams = audio.streamDiagnostics();
    // Two locks, not one: an html5 pool of 0 means <audio> elements are still
    // locked, whatever Web Audio is doing. See AudioManager.unlockState.
    const unlock = audio.unlockState();
    const stacked = audio.stackedClips();

    return (
        <div className={styles.overlay}>
            <div className={styles.header}>
                <span className={styles.title}>AUDIO</span>
                {/* So a photo of this panel alone says which box produced it. */}
                <span className={styles.device}>{deviceLabel()}</span>
                <span className={styles.budget} data-warn={stats.failed > 0 || counts.loadError > 0}>
                    {stats.loaded}/{stats.clips} loaded · {mb(stats.decodedBytes)}/{mb(stats.budgetBytes)} decoded
                    {stats.streaming > 0 && ` · ${stats.streaming} streaming`}
                </span>
            </div>

            <div className={styles.row}>
                <span>ctx <b>{ctxState}</b></span>
                <span>stream <b>{stats.streamingMode ? "on" : "off"}</b></span>
                <span>music <b>{audio.musicMuted ? "muted" : "on"}</b></span>
                <span>sfx <b>{audio.sfxMuted ? "muted" : "on"}</b></span>
                <span>vo <b>{audio.voMuted ? "muted" : "on"}</b></span>
                <span data-warn={!unlock.webAudio}>webaudio <b>{unlock.webAudio ? "unlocked" : "LOCKED"}</b></span>
                <span data-warn={unlock.html5Pool === 0}>html5pool <b>{unlock.html5Pool}</b></span>
                <span>input <b>{unlock.sawInput ? "yes" : "no"}</b></span>
            </div>

            <div className={styles.row}>
                <span>playing m{audio.activeMusic.length} s{audio.activeSfx.length} v{audio.activeVo.length}</span>
                {stacked.map(c => (
                    <span key={c.name} data-warn>stacked <b>{c.name} x{c.count}</b></span>
                ))}
                <span className={styles.sinceLabel}>totals since load:</span>
                <span data-warn={counts.loadError > 0}>load-fail <b>{counts.loadError}</b></span>
                <span data-warn={counts.playError > 0}>play-fail <b>{counts.playError}</b></span>
                <span data-warn={stats.failed > 0}>empty <b>{stats.failed}</b></span>
                <span data-warn={stats.loading > 0}>loading <b>{stats.loading}</b></span>
                <span>evicted <b>{counts.evicted}</b></span>
            </div>

            {streams.length > 0 && (
                <div className={styles.streams}>
                    {streams.map(s => (
                        <div key={s.name} className={styles.stream} data-warn={s.state !== "loaded"}>
                            <span className={styles.clip}>{s.name}</span>
                            <span className={styles.type}>{s.state}</span>
                            <span>rs {named(READY_STATE, s.readyState)}</span>
                            <span>ns {named(NETWORK_STATE, s.networkState)}</span>
                            <span className={styles.message}>{s.events || "no media events"}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.events}>
                {events.length === 0 && <div className={styles.empty}>no errors — audio is loading cleanly</div>}
                {events.map(e => (
                    <div key={e.id} className={styles.event} data-level={e.level}>
                        <span className={styles.time}>{(e.t / 1000).toFixed(1)}s</span>
                        <span className={styles.type}>{e.type}</span>
                        <span className={styles.clip}>{e.clip}</span>
                        <span className={styles.message}>{e.message}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
